import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppData } from '../../contexts/AppDataContext';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { useSuggestionWizard } from '../../contexts/SuggestionWizardContext';
import { apiClient } from '../../lib/api';
import { IdealCustomerProfile } from '../../types';
import { MobileHeader, MobileDrawer, MobileSearchModal } from './mobile';

interface MobileNavProps {
  selectedDatasetId?: string;
}

export const MobileNav: React.FC<MobileNavProps> = ({ selectedDatasetId }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const { openSettings } = useSettingsModal();
  const { openWizard, openWithIcp } = useSuggestionWizard();
  const { datasets, creditData, subscriptionData } = useAppData();

  const [icpProfiles, setIcpProfiles] = useState<IdealCustomerProfile[]>([]);
  const [expandedIcpIds, setExpandedIcpIds] = useState<Set<string>>(new Set());
  const [isICPSectionExpanded, setIsICPSectionExpanded] = useState(true);

  useEffect(() => {
    const fetchICPs = async () => {
      try {
        const profiles = await apiClient.getICPProfiles();
        setIcpProfiles(profiles || []);
      } catch (error) {
        console.error('Error fetching ICP profiles:', error);
      }
    };
    fetchICPs();
  }, []);

  useEffect(() => {
    setIsOpen(false);
    // location.search is a dependency too: admin-tab navigation changes ONLY the
    // query (?view=...), and a drawer keyed on pathname alone stays stuck open
    // with body scroll locked — the page reads as frozen on mobile.
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const toggleIcpExpanded = (icpId: string) => {
    setExpandedIcpIds(prev => {
      const next = new Set(prev);
      if (next.has(icpId)) {
        next.delete(icpId);
      } else {
        next.add(icpId);
      }
      return next;
    });
  };

  return (
    <>
      <MobileHeader onOpenMenu={() => setIsOpen(true)} />

      <MobileDrawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        datasets={datasets || []}
        selectedDatasetId={selectedDatasetId}
        icpProfiles={icpProfiles}
        expandedIcpIds={expandedIcpIds}
        isICPSectionExpanded={isICPSectionExpanded}
        creditData={creditData}
        planName={subscriptionData?.plan_name || 'Free'}
        onToggleIcp={toggleIcpExpanded}
        onToggleICPSection={() => setIsICPSectionExpanded(!isICPSectionExpanded)}
        onOpenSearch={() => setShowSearchModal(true)}
        onOpenWizard={openWizard}
        onOpenWithIcp={openWithIcp}
        onOpenBilling={() => {
          setIsOpen(false);
          openSettings('billing');
        }}
      />

      <MobileSearchModal
        isOpen={showSearchModal}
        datasets={datasets || []}
        onClose={() => setShowSearchModal(false)}
        onCloseDrawer={() => setIsOpen(false)}
      />
    </>
  );
};
